import React from "react";
import { createPortal } from "react-dom";
import useDialogo from "../UI/Modal/useDialogo";
import "../../styles/hc-modal.css";

/* ============================================================================
   HCModal — base común de los modales de Historia Clínica.

   Hermano de UI/Modal, no sustituto: aquél es para confirmar, avisar o pedir
   de 1 a 4 campos (máx. 720 px, sin tabs, sin scroll interno) y lo usan 20
   pantallas. Éste es para los formularios largos de la consulta: 960 px,
   pestañas, cuerpo con scroll propio y pie con validación en vivo.

   El comportamiento delicado (pila de diálogos, Esc y clic fuera con aviso de
   cambios sin guardar, trampa de foco, foco devuelto al abridor) NO se
   reimplementa: sale de `useDialogo`, el mismo que usa UI/Modal.

   Anatomía fija, no negociable por quien lo use:
   · Cabecera fija — no scrollea.
   · Tabs debajo de la cabecera, fuera del cuerpo.
   · Cuerpo con scroll propio; todas las secciones visibles (cero acordeones).
   · Pie sticky — el primario SIEMPRE visible.
   ========================================================================== */

const TAMANOS = { sm: "sm", md: "md", lg: "lg" };

function IconoAlerta({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <path d="M12 16.5h.01" />
    </svg>
  );
}
function IconoCheck({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12.5l5.5 5.5L20 7" />
    </svg>
  );
}

export default function HCModal({
  isOpen,
  size = "md",
  title,
  subtitle,
  meta,
  tabs,
  activeTab,
  onTabChange,
  validation, // { missing: ['Motivo'] }
  primaryLabel = "Guardar",
  onPrimary,
  primaryLoading = false,
  secondaryAction, // { label, onClick, disabled }
  cancelLabel = "Cancelar",
  onCancel,
  onClose,
  hayCambiosSinGuardar = false,
  children,
}) {
  const ancho = TAMANOS[size] || "md";
  const { cajaRef, intentarCerrar } = useDialogo({
    abierto: isOpen,
    hayCambiosSinGuardar,
    onCerrar: onClose,
  });

  if (!isOpen) return null;

  const faltan = validation?.missing || [];
  const hayFaltantes = faltan.length > 0;

  const contenido = (
    <div
      className="hcm__overlay"
      data-testid="hcmodal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) intentarCerrar();
      }}
    >
      <div
        className={`hcm hcm--${ancho}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="hcmodal"
        ref={cajaRef}
        tabIndex={-1}
      >
        <div className="hcm__head">
          <div className="hcm__head-txt">
            <h2 className="hcm__title">{title}</h2>
            {subtitle && <div className="hcm__subtitle">{subtitle}</div>}
          </div>
          {meta && meta.length > 0 && <div className="hcm__meta">{meta}</div>}
          <button type="button" className="hcm__close" aria-label="Cerrar" onClick={intentarCerrar}>
            ✕
          </button>
        </div>

        {tabs && tabs.length > 1 && (
          <div className="hcm__tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                className={"hcm__tab " + (activeTab === t.key ? "hcm__tab--active" : "")}
                onClick={() => onTabChange && onTabChange(t.key)}
              >
                {t.label}
                {t.count > 0 && <span className="hcm__tab-count">{t.count}</span>}
              </button>
            ))}
          </div>
        )}

        <div className="hcm__body">{children}</div>

        <div className="hcm__foot">
          {hayFaltantes ? (
            <span className="hcm__estado hcm__estado--falta" role="status">
              <IconoAlerta />
              {faltan.length === 1
                ? `Falta 1 campo obligatorio: ${faltan[0]}`
                : `Faltan ${faltan.length} campos obligatorios: ${faltan.join(", ")}`}
            </span>
          ) : (
            <span className="hcm__estado hcm__estado--listo" role="status">
              <IconoCheck />
              Listo para guardar
            </span>
          )}

          <div className="hcm__foot-grow" />

          {secondaryAction && (
            <button
              type="button"
              className="hcbtn hcbtn--link"
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled || hayFaltantes || primaryLoading}
            >
              {secondaryAction.label}
            </button>
          )}
          <button type="button" className="hcbtn" onClick={onCancel || intentarCerrar}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="hcbtn hcbtn--primario"
            onClick={onPrimary}
            disabled={hayFaltantes || primaryLoading}
            title={hayFaltantes ? `Falta completar: ${faltan.join(", ")}` : undefined}
          >
            {primaryLoading ? "Guardando…" : primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(contenido, document.body);
}
